import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import { beSalonSetupSupportedRead } from 'actions/signup/beSalonSetupSupported.action';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import S from 'constants/string.const';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import asArray from 'utils/arrays/asArray.util';
import call from 'utils/functions/call.util';

import CN from './SalonSetupLocationFields.module.scss';

const MIN_TZ_FOR_DROPDOWN = 2;

export const SalonSetupLocationFields = ({ onChange }) => {
  const resultLocationRead = usePrepareReadOnly(beSalonSetupSupportedRead);
  const { qj, isLoading } = resultLocationRead ?? {};

  const { t } = useTranslation();

  const countries = useMemo(() => asArray(qj?.countries), [qj]);

  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState('');

  const { val: isCountryOpen, nay: closeCountry, tip: toggleCountry } = useTip(false);
  const { val: isTimezoneOpen, nay: closeTz, tip: toggleTz } = useTip(false);

  const selectedCountry = useMemo(() => countries.find((c) => c.code === selectedCountryCode), [countries, selectedCountryCode]);

  const timezoneOptions = selectedCountry?.supportedTimezones || [];
  const currentTimezone = timezoneOptions.length ? selectedTimezone || timezoneOptions[0] : '';
  const renderTimezoneDropdown = timezoneOptions.length >= MIN_TZ_FOR_DROPDOWN;

  const handleCountrySelect = (code) => {
    setSelectedCountryCode(code);
    const country = countries.find((c) => c.code === code);
    const defaultTz = country?.supportedTimezones?.[0] || '';
    setSelectedTimezone(defaultTz);
    closeCountry();

    call('SalonSetupLocationFields.onChange', onChange, { country: code, timezone: defaultTz });
  };

  const handleTimezoneSelect = (tz) => {
    setSelectedTimezone(tz);
    closeTz();

    call('SalonSetupLocationFields.onChange', onChange, { country: selectedCountryCode, timezone: tz });
  };

  useEffect(() => {
    if (!countries.length) return;

    if (selectedCountryCode) return;

    const uk = countries.find((c) => c.code === 'GB');
    if (!uk) return;

    const defaultTz = uk.supportedTimezones?.[0] || '';

    setSelectedCountryCode('GB');
    setSelectedTimezone(defaultTz);

    call('SalonSetupLocationFields.onChange', onChange, {
      country: 'GB',
      timezone: defaultTz,
    });
  }, [countries, selectedCountryCode, onChange]);

  return (
    <div data-bem="SalonSetupLocationFields" className={CN.component}>
      {isLoading && <OverlaySpinner />}

      {/* Country dropdown */}
      <div data-bem="SalonSetupLocationFields__country-dropdown" className={CN.container}>
        <div className={CN.dropdownLabel}>
          {t('register.salonSetup.countryLabel')}
          {S.colon}
        </div>
        <div data-bem="SalonSetupLocationFields__dropdown--container--country" className={CN.dropdownContainer}>
          <div data-bem="SalonSetupLocationFields__dropdown--button--country" onClick={toggleCountry}>
            <div
              data-bem="SalonSetupLocationFields__dropdown--selected--country"
              className={cn({
                [CN.dropdownButton]: true,
                [CN.active]: isCountryOpen,
              })}
            >
              {selectedCountryCode ? selectedCountry?.countryLabel : t('register.salonSetup.countryPlaceholder')}
              <Icon variant="arrowDown" width="lg" />
            </div>
          </div>
          {isCountryOpen && (
            <DropdownFoldout tag="SalonSetupLocationFields__dropdown--country" width="full" onClickOutside={toggleCountry}>
              <DropdownFoldout.Body>
                <div data-bem="SalonSetupLocationFields__dropdown--content--country" className={CN.dropdownContent}>
                  {countries.map((c) => (
                    <div data-bem="SalonSetupLocationFields__dropdown--item--country" key={c.code} className={CN.item} onClick={() => handleCountrySelect(c.code)}>
                      {c.countryLabel}
                    </div>
                  ))}
                </div>
              </DropdownFoldout.Body>
            </DropdownFoldout>
          )}
        </div>
      </div>

      {/* Timezone dropdown */}
      {renderTimezoneDropdown && (
        <div data-bem="SalonSetupLocationFields__tz-dropdown" className={CN.container}>
          <div className={CN.dropdownLabel}>
            {t('register.salonSetup.timezoneLabel')}
            {S.colon}
          </div>
          <div data-bem="SalonSetupLocationFields__dropdown--container--tz" className={CN.dropdownContainer}>
            <div data-bem="SalonSetupLocationFields__dropdown--button--tz" onClick={toggleTz}>
              <div
                data-bem="SalonSetupLocationFields__dropdown--selected--tz"
                className={cn({
                  [CN.dropdownButton]: true,
                  [CN.active]: isTimezoneOpen,
                })}
              >
                {currentTimezone || t('register.salonSetup.timezonePlaceholder')}
                <Icon variant="arrowDown" width="lg" />
              </div>
            </div>
            {isTimezoneOpen && (
              <DropdownFoldout tag="SalonSetupLocationFields__dropdown--tz" width="full" onClickOutside={toggleTz}>
                <DropdownFoldout.Body>
                  <div data-bem="SalonSetupLocationFields__dropdown--content--tz" className={CN.dropdownContent}>
                    {timezoneOptions.map((tz) => (
                      <div data-bem="SalonSetupLocationFields__dropdown--item--tz" key={tz} className={CN.item} onClick={() => handleTimezoneSelect(tz)}>
                        {tz}
                      </div>
                    ))}
                  </div>
                </DropdownFoldout.Body>
              </DropdownFoldout>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
